/**
 * Konkord: short game sounds made on the spot (Web Audio), no sound files.
 * On by default; the choice is kept on the device.
 */
import { createSignal } from "solid-js";

const KEY = "konkord.games.sound";
const VOLUME = 0.32;

function readOn() {
  try {
    return localStorage.getItem(KEY) !== "off";
  } catch {
    return true;
  }
}

const [soundOn, setSoundOnSignal] = createSignal(readOn());
export { soundOn };

export function setSoundOn(on: boolean) {
  setSoundOnSignal(on);
  try {
    localStorage.setItem(KEY, on ? "on" : "off");
  } catch {
    // the choice lasts until the app closes
  }
}

let context: AudioContext | undefined;

function audio() {
  if (!soundOn() || typeof AudioContext === "undefined") return;
  context ??= new AudioContext();
  if (context.state === "suspended") void context.resume().catch(() => {});
  return context;
}

/**
 * One tone with a quick attack and a soft fade
 */
function tone(
  ctx: AudioContext,
  frequency: number,
  start: number,
  length: number,
  { type = "sine" as OscillatorType, volume = 1, slideTo = 0 } = {},
) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(frequency, start);
  if (slideTo) osc.frequency.exponentialRampToValueAtTime(slideTo, start + length);
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(VOLUME * volume, start + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + length);
  osc.connect(gain).connect(ctx.destination);
  osc.start(start);
  osc.stop(start + length + 0.02);
}

export const gameSounds = {
  /** a cell (or a few) opened */
  open(cells: number) {
    const ctx = audio();
    if (!ctx) return;
    const now = ctx.currentTime;
    if (cells <= 3) {
      tone(ctx, 520 + Math.random() * 80, now, 0.09, { type: "triangle", volume: 0.7, slideTo: 380 });
      return;
    }
    // a whole area: a quick rising sparkle, longer for bigger areas
    const notes = Math.min(6, 2 + Math.floor(cells / 10));
    for (let i = 0; i < notes; i++) {
      tone(ctx, 440 * 2 ** (i * 4 / 12), now + i * 0.035, 0.12, { type: "triangle", volume: 0.45 });
    }
  },
  /** flag placed */
  flag() {
    const ctx = audio();
    if (!ctx) return;
    const now = ctx.currentTime;
    tone(ctx, 880, now, 0.05, { type: "square", volume: 0.18 });
    tone(ctx, 1320, now + 0.045, 0.07, { type: "square", volume: 0.14 });
  },
  /** "?" or nothing again */
  unflag() {
    const ctx = audio();
    if (!ctx) return;
    tone(ctx, 660, ctx.currentTime, 0.06, { type: "square", volume: 0.14, slideTo: 440 });
  },
  boom() {
    const ctx = audio();
    if (!ctx) return;
    const now = ctx.currentTime;
    // noise through a closing filter, over a low thump
    const length = 0.7;
    const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * length), ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / data.length) ** 2;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(2400, now);
    filter.frequency.exponentialRampToValueAtTime(120, now + length);
    const gain = ctx.createGain();
    gain.gain.value = VOLUME * 1.4;
    noise.connect(filter).connect(gain).connect(ctx.destination);
    noise.start(now);
    tone(ctx, 90, now, 0.5, { volume: 1.2, slideTo: 40 });
  },
  win() {
    const ctx = audio();
    if (!ctx) return;
    const now = ctx.currentTime;
    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) =>
      tone(ctx, f, now + i * 0.11, i === 3 ? 0.5 : 0.18, { type: "triangle", volume: 0.6 }),
    );
  },
  /** a click that does nothing (game over, not your turn) */
  nope() {
    const ctx = audio();
    if (!ctx) return;
    const now = ctx.currentTime;
    tone(ctx, 220, now, 0.08, { type: "square", volume: 0.16 });
    tone(ctx, 180, now + 0.09, 0.12, { type: "square", volume: 0.16 });
  },
  /** your turn */
  turn() {
    const ctx = audio();
    if (!ctx) return;
    const now = ctx.currentTime;
    tone(ctx, 784, now, 0.12, { volume: 0.55 });
    tone(ctx, 1175, now + 0.1, 0.2, { volume: 0.55 });
  },
};
